import React from 'react';
import { Chip, Button, Tooltip } from '@mui/material';
import { Launch as LaunchIcon } from '@mui/icons-material';
import { DataManagementConfig, TableColumn } from '@shared/components/data-management/types';
import { AddSurveyDialog } from '../components';
import { SurveyTableData, SurveyFormData } from '../types';
import { useActiveSurveysForDataManagement } from '../hooks/useActiveSurveysForDataManagement';
import { useUpdateSurvey, useCloseSurvey } from '../hooks/useSurveys';
import { notificationService } from '@shared/components/notifications/notificationService';

/**
 * Creates table configuration for ACTIVE surveys
 * These are published surveys that can accept responses
 */
export const createActiveSurveysConfig = (
  subjectId: string,
  tenantId: string,
  domainId: string,
  navigate?: (path: string) => void,
  closeSurveyMutation?: ReturnType<typeof useCloseSurvey>
): DataManagementConfig<SurveyTableData, SurveyFormData, SurveyFormData> => {
  const useActiveSurveysQuery = () => useActiveSurveysForDataManagement(subjectId);
  const useUpdateSurveyWrapper = () => useUpdateSurvey(subjectId);

  // Close Survey action
  const closeSurveyAction = {
    label: 'Close',
    onClick: async (row: SurveyTableData) => {
      if (!closeSurveyMutation) {
        console.error('Close survey mutation not provided');
        return;
      }
      try {
        await closeSurveyMutation.mutateAsync(row.id);
      } catch (error) {
        console.error('Failed to close survey:', error);
        notificationService.error('Failed to close survey. Please try again.');
      }
    },
    color: 'warning' as const,
    variant: 'outlined' as const,
  };

  return {
    hooks: {
      useQuery: useActiveSurveysQuery,
      useUpdate: useUpdateSurveyWrapper,
    },

    table: {
      title: 'Active Surveys',
      columns: [
        {
          label: 'Title',
          key: 'title',
          sx: {
            minWidth: '25%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        },
        {
          label: 'Status',
          key: 'status',
          render: () => <Chip label="Active" color="success" variant="outlined" size="small" />,
        },
        {
          label: 'Go to survey',
          key: 'id',
          render: (_value, row: SurveyTableData) => (
            <Tooltip title="Launch survey instance">
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<LaunchIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  if (navigate) {
                    navigate(`/app/surveys/${row.id}/instance`);
                  } else {
                    window.location.href = `/app/surveys/${row.id}/instance`;
                  }
                }}
                sx={{ minWidth: 'auto' }}
              >
                Go to survey
              </Button>
            </Tooltip>
          ),
        },
        {
          label: 'Created',
          key: 'createdAt',
          render: (value: string) => new Date(value).toLocaleDateString(),
        },
      ] as TableColumn<SurveyTableData>[],
      emptyMessage: '🚀 No active surveys yet!\nPublish a draft to get started.',
      errorMessage: 'Failed to load active surveys. Please try again.',
    },

    actions: {
      create: {
        label: 'Add Survey',
        // eslint-disable-next-line react/display-name -- Anonymous component in table config object
        dialog: React.memo<{
          open: boolean;
          onClose: () => void;
          editingItem?: SurveyTableData | null;
          onSuccess?: () => void;
        }>((props) => (
          <AddSurveyDialog
            {...props}
            subjectId={subjectId}
            tenantId={tenantId}
            domainId={domainId}
          />
        )),
      },
      edit: {
        label: 'Edit',
      },
      custom: [closeSurveyAction],
    },

    features: {
      search: true,
      pagination: true,
      onRowClick: (row: SurveyTableData) => {
        if (navigate) {
          navigate(`/app/surveys/${row.id}/questions`);
        } else {
          window.location.href = `/app/surveys/${row.id}/questions`;
        }
      },
    },
  };
};
